import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { Warning } from '@mui/icons-material';

interface DeleteConfirmationDialogProps {
  open: boolean;
  reportTitle: string;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  reportTitle,
  onConfirm,
  onCancel,
}) => {
  const [deleteText, setDeleteText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (deleteText !== 'delete') {
      setError('Please type "delete" to confirm');
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await onConfirm();
      handleClose();
    } catch (error) {
      setError('Failed to delete report. Please try again.');
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setDeleteText('');
    setError(null);
    setIsDeleting(false);
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Warning color="error" />
          <Typography variant="h6">Delete Report</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to delete the report "<strong>{reportTitle}</strong>"?
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This action will permanently delete:
        </Typography>
        
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" color="text.secondary">
            The report and all its data
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            All entries associated with this report
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            All images in the entries (from Firestore, storage cleanup may be needed)
          </Typography>
        </Box>
        
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          <strong>This action cannot be undone.</strong>
        </Typography>
        
        <TextField
          fullWidth
          label='Type "delete" to confirm'
          value={deleteText}
          onChange={(e) => setDeleteText(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={isDeleting}
          autoFocus
        />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={deleteText !== 'delete' || isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} /> : null}
        >
          {isDeleting ? 'Deleting...' : 'Delete Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
